/**
 * 見た目のトークン(UX1-a)。色・フォント・角丸は**ここだけ**で決める。
 *
 * antd には `antdTheme` で、素の CSS(`shell.css` / `index.css`)には `applyCssVars()`
 * が `:root` に置く `--ux-*` 変数で届ける。画面のコードに色の文字列を直書きしない
 * (色は後で変わる。変えるときはこのファイルの `tokens` だけ触る)。
 */
import type { ThemeConfig } from "antd";

export const tokens = {
  primary: "#1D5FA8",
  primaryHover: "#164A85",
  /** アイコンレール(左端 64px) */
  rail: "#16213A",
  railSelected: "#2A3B5F",
  railIcon: "#AFC0D9",
  railIconDisabled: "#4F5F7E",
  railAvatar: "#3E6FB0",
  /** 地・面・罫 */
  bg: "#F4F6F8",
  surface: "#FFFFFF",
  surfaceMuted: "#F8FAFC",
  border: "#E3E8EF",
  borderInput: "#D5DBE4",
  divider: "#EEF1F5",
  /** 文字 */
  text: "#1B2430",
  textNav: "#3A4556",
  textSecondary: "#5B6779",
  textTertiary: "#7A8699",
  textDisabled: "#9AA5B5",
  /** 状態色と、その薄い地 */
  success: "#1E8E5A",
  successBg: "#E6F4EE",
  warning: "#B7791F",
  warningBg: "#FDF1E3",
  warningText: "#8A5A0E",
  danger: "#C5302C",
  dangerBg: "#FBE9E8",
  dangerText: "#A3261F",
  infoBg: "#E8F0FA",
  /** ナビの選択行 */
  navSelectedBg: "#EAF1FB",
  /** アプリの印(ホームのタイル) */
  appHr: "#1D5FA8",
  appHrBg: "#E8F0FA",
  appIdp: "#1E8E5A",
  appIdpBg: "#E6F4EE",
  appMdm: "#6B46C1",
  appMdmBg: "#F1ECFA",
  appOther: "#3A4556",
  appOtherBg: "#F4F6F8",
  // Web フォントは読み込まない。OS の標準 sans を Mac / Windows の順で引く(旧 index.css と同じ)
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Hiragino Sans", "Noto Sans JP", Meiryo, sans-serif',
  radius: 8,
  radiusLg: 12,
  /** 外殻の本文余白と上バーの高さ(ui.css の `.ui-list` / `.ui-panel` / `.ui-detail` が参照)*/
  contentPad: 24,
  topbarH: 52,
} as const;

/** `ConfigProvider theme` に渡す。 */
export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: tokens.primary,
    colorPrimaryHover: tokens.primaryHover,
    colorInfo: tokens.primary,
    colorLink: tokens.primary,
    colorLinkHover: tokens.primaryHover,
    colorSuccess: tokens.success,
    colorWarning: tokens.warning,
    colorError: tokens.danger,
    colorBgLayout: tokens.bg,
    colorBgContainer: tokens.surface,
    colorBorder: tokens.borderInput,
    colorBorderSecondary: tokens.border,
    colorSplit: tokens.border,
    colorText: tokens.text,
    colorTextSecondary: tokens.textSecondary,
    colorTextTertiary: tokens.textTertiary,
    colorTextDisabled: tokens.textDisabled,
    fontFamily: tokens.fontFamily,
    borderRadius: tokens.radius,
    borderRadiusLG: tokens.radiusLg,
  },
  components: {
    Layout: { bodyBg: tokens.bg, headerBg: tokens.surface, siderBg: tokens.surface },
    Menu: {
      itemHeight: 36,
      itemMarginBlock: 2,
      itemMarginInline: 0,
      itemPaddingInline: 12,
      itemBorderRadius: tokens.radius,
      itemColor: tokens.textNav,
      itemSelectedBg: tokens.navSelectedBg,
      itemSelectedColor: tokens.primary,
      groupTitleFontSize: 11,
      groupTitleColor: tokens.textTertiary,
      activeBarBorderWidth: 0,
      iconMarginInlineEnd: 10,
    },
    Card: { borderRadiusLG: tokens.radiusLg, headerFontSize: 15 },
    Tooltip: { colorBgSpotlight: tokens.rail },
  },
};

/**
 * 素の CSS が参照する変数を `:root` に置く(`shell.css` の `var(--ux-*)`)。
 * 起動時に 1 回呼ぶ。色の正は `tokens` なので、CSS 側に色を書かない。
 */
export function applyCssVars(root: HTMLElement = document.documentElement): void {
  for (const [key, value] of Object.entries(tokens)) {
    const name = `--ux-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
    root.style.setProperty(name, typeof value === "number" ? `${value}px` : value);
  }
}
